import { Link, NavLink, withRouter } from 'react-router-dom'
import styled from 'styled-components'
import { Icon, CDNAsset, Text } from '@navinc/base-react-components'
import { UserSettings } from './user-settings'
import { whenSidebarFits } from '../theme'
import { useCapabilities } from '../hooks/apollo/use-capabilities'

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-flow: column nowrap;
`

const StyledLink = styled(Link)`
  display: none;
  padding: 16px 16px 16px 16px;
  margin-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.navSecondary};
  @media (${whenSidebarFits}) {
    display: block;
  }
`

const Nav = styled.nav`
  flex-grow: 1;
  grid-column: 1 / -1;
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  padding-top: 24px;

  @media (${whenSidebarFits}) {
    padding-top: 0;
  }
`

const List = styled.ul`
  padding: 0;
`

const StyledIcon = styled(Icon).attrs(
  () =>
    ({ theme }) =>
      theme.navSecondary
)`
  max-width: 20px;
  max-height: 20px;
`

const StyledMenuLink = styled(Text)`
  font-weight: 700;
  font-size: 14px;
  -webkit-font-smoothing: antialiased;
  width: 100%;
  padding: 8px 16px;
  color: ${({ theme }) => theme.navSecondary};
  display: grid;
  grid-template-columns: 24px 1fr auto;
  gap: 16px;
  align-items: center;
  align-content: center;

  & > ${StyledIcon} {
    margin-right: 16px;
    font-size: 14px;
  }

  &.is-active,
  &:focus {
    font-weight: 0;
    color: ${({ theme }) => theme.navPrimary400};
    background: ${({ theme }) =>
      `${theme.navPrimary300} radial-gradient(circle, transparent 1%, ${theme.navPrimary700} 1%) center/15000%`};
  }

  &:hover {
    color: inherit;
    color: ${({ theme }) => theme.navSecondary};
    background-color: ${({ theme }) => theme.navPrimary400};
    transition: ease-out background 0.4s;
  }
`

const StyledNavLink = styled(StyledMenuLink).attrs(() => ({ as: NavLink }))``

// eslint-disable-next-line import/no-unused-modules
export const isActive = (match, { pathname = '' } = {}) => pathname.includes('/partner/reports')

const _SideNav = ({ onNavigate }) => {
  const { isMarketPlaceCCsEnabled, isMarketPlaceLoansEnabled, canEditBusinessIdentity, isCreditReportsEnabled } =
    useCapabilities()

  return (
    <Wrapper>
      <StyledLink data-testid="ent:nav:side-nav-logo" to="/partner/home">
        <CDNAsset directory="logos" filename="nav-logo-tan.svg" />
      </StyledLink>
      <Nav>
        <List>
          <li>
            <StyledNavLink
              data-testid="ent:nav:side-nav-overview"
              onClick={onNavigate}
              activeClassName="is-active"
              to="/partner/home"
            >
              <StyledIcon name="data/chart-up" />
              Overview
            </StyledNavLink>
          </li>
          {isCreditReportsEnabled && (
            <li>
              <StyledNavLink
                data-testid="ent:nav:side-nav-business-scores"
                onClick={onNavigate}
                activeClassName="is-active"
                to="/partner/reports"
                isActive={isActive}
              >
                <StyledIcon name="tech/dashboard" />
                Credit Reports
              </StyledNavLink>
            </li>
          )}

          {isMarketPlaceCCsEnabled && (
            <li>
              <StyledNavLink
                data-testid="ent:nav:side-nav-business-credit-cards"
                onClick={onNavigate}
                activeClassName="is-active"
                to="/partner/financing/credit-cards"
              >
                <StyledIcon name="financing/credit-card" />
                Credit Cards
              </StyledNavLink>
            </li>
          )}
          {isMarketPlaceLoansEnabled && (
            <li>
              <StyledNavLink
                data-testid="ent:nav:side-nav-business-financing"
                onClick={onNavigate}
                activeClassName="is-active"
                to="/partner/financing/loans"
              >
                <StyledIcon name="financing/money-bag" />
                Financing
              </StyledNavLink>
            </li>
          )}
          {canEditBusinessIdentity && (
            <li>
              <StyledNavLink
                data-testid="ent:nav:side-nav-business-profile"
                onClick={onNavigate}
                activeClassName="is-active"
                to="/partner/profile"
              >
                <StyledIcon name="buildings/business" />
                Business profile
              </StyledNavLink>
            </li>
          )}
        </List>
        <UserSettings onNavigate={onNavigate} />
      </Nav>
    </Wrapper>
  )
}

export const SideNav = withRouter(_SideNav)
